import { type AttachmentCategory, isCustomerRole } from "@nlc/shared-types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { AfterHoursBanner } from "@/components/after-hours-banner";
import { MediaPreviewModal } from "@/components/media-preview-modal";
import { useEditRequestModals } from "@/features/request/edit/contexts/edit-request-modals.context";
import { useTemplateLock } from "@/features/request/edit/hooks/useTemplateLock.tsx";
import FeedbackToolComponent from "@/features/request/feedback-tool";
import { FeedbackToolProvider } from "@/features/request/feedback-tool/feedbackTool/components/FeedbackToolProvider.tsx";
import { collaboratorsKeys, requestKeys } from "@/hooks/queryKeys";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";
import { useRequestId } from "@/hooks/useRequestId.tsx";
import { useUser } from "@/hooks/useUser";
import { isAfterHours } from "@/utils/afterHours";

import type { ChatStory } from "./types";

import { ChatInput } from "./components/chat-input";
import { ChatSkeleton } from "./components/ChatSkeleton";
import { LoadingIndicator } from "./components/LoadingIndicator";
import { MessageList } from "./components/MessageList";
import { useChatDataEvent } from "./hooks/useChatDataEvent";
import { useCreateChatComment } from "./hooks/useCreateChatComment";
import { useDeleteChatAttachment } from "./hooks/useDeleteChatAttachment";
import { useDismissAfterHoursBanner } from "./hooks/useDismissAfterHoursBanner";
import { useFilePreview } from "./hooks/useFilePreview";
import { useFileUpload } from "./hooks/useFileUpload";
import { useInfiniteScroll } from "./hooks/useInfiniteScroll";
import { useScrollManager } from "./hooks/useScrollManager";
import { useUpdateChatComment } from "./hooks/useUpdateChatComment";
import { getAttachmentDisplayName } from "./utils/attachment-display-name";
import { parseAllChatMessages } from "./utils/message-parsers";

export type { GetChatStoriesResponse } from "./schemas/chat-schemas";

function shouldCreateNewMessage(
  messageContent: string,
  message: ChatStory | undefined,
) {
  return messageContent.trim().length > 0 && !message?.id;
}

export function ChatSection() {
  const [messageContent, setMessageContent] = useState("");
  const [isFeedbackToolOpen, setIsFeedbackToolOpen] = useState<boolean>(false);
  const [feedbackAttachment, setFeedbackAttachment] = useState<{
    id: number;
    name: string;
    path: string;
    url: string;
  } | null>(null);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);
  const { currentProject, data: user } = useUser();
  const { data: request } = useGetRequest();
  const { isTemplate } = useIsTemplate();
  const { isLocked } = useTemplateLock();
  const { openUnlock } = useEditRequestModals();

  const requestId = Number(useRequestId() ?? request?.id);

  const { isLoading: isChatConnecting, stories: rawMessages } =
    useChatDataEvent({ requestId });
  const allMessages = useMemo(
    () => parseAllChatMessages(rawMessages),
    [rawMessages],
  );

  const isCostumer = isCustomerRole(user?.user.roleId || 0);
  const timeZone = user?.user.timezone;

  const [afterHoursDismissed, setAfterHoursDismissed] = useState(false);
  const dismissAfterHoursBanner = useDismissAfterHoursBanner();

  const showAfterHoursBanner =
    isAfterHours(new Date()) &&
    isCostumer &&
    !(currentProject?.sunset ?? false) &&
    !user?.user.afterHoursBannerDismissed &&
    !afterHoursDismissed;

  const handleAfterHoursDismiss = () => setAfterHoursDismissed(true);
  const handleAfterHoursDismissPermanently = () => {
    dismissAfterHoursBanner.mutate();
    setAfterHoursDismissed(true);
  };

  const {
    attachmentList,
    currentAttachmentId,
    handleClosePreview,
    handleOpenPreview,
    selectedAttachment,
  } = useFilePreview({ rawMessages });

  // Convert attachmentList to mediaItems
  const mediaItems = useMemo(
    () =>
      attachmentList.map((story) => ({
        category: story.attachment?.category as AttachmentCategory,
        extension: story.attachment?.path?.split(".").pop() || "",
        id: story.attachment?.id?.toString() || "",
        name: getAttachmentDisplayName(story.attachment),
        url: story.attachment?.url || "",
      })),
    [attachmentList],
  );

  const createComment = useCreateChatComment({ requestId });
  const { cancelEdit, editMode, isEditing, startEdit, updateComment } =
    useUpdateChatComment({
      rawMessages,
      requestId,
      userId: user?.user.id || 0,
    });

  const {
    addExistingFiles,
    addFiles,
    clearAll,
    files,
    isUploading,
    removeFile,
    retryUpload,
    successfulUploads,
  } = useFileUpload();

  const deleteAttachment = useDeleteChatAttachment();

  const queryClient = useQueryClient();

  useEffect(() => {
    if (
      editMode.commentBatchId &&
      editMode.isEditing &&
      editMode.messages.length > 0
    ) {
      const messages = editMode.messages.filter(
        (message) => message.asanaResourceType === "story",
      );
      const attachments = editMode.messages.filter(
        (message) => message.asanaResourceType === "attachment",
      );

      if (messages.length > 0) {
        setMessageContent(messages[0]?.comment || "");
      }

      if (attachments.length > 0) {
        const existingFiles = attachments
          .filter((attachment) => attachment.attachment)
          .map((attachment: ChatStory) => {
            const mockFile = new File(
              [],
              attachment.attachment?.name || "attachment",
              { type: "application/octet-stream" },
            );

            return {
              attachmentId: attachment.attachment?.id || 0,
              file: mockFile,
            };
          });

        clearAll();
        addExistingFiles(existingFiles);
      } else {
        clearAll();
      }
    }
  }, [editMode, clearAll, addExistingFiles]);

  const handleCancelEdit = () => {
    cancelEdit();
    setMessageContent("");
    clearAll();
  };

  const { hasMoreItems, isLoading, loadingRef, visibleItemCount } =
    useInfiniteScroll({
      containerRef: chatContainerRef,
      totalItems: allMessages.length,
    });

  const messages = allMessages.slice(-visibleItemCount);

  useScrollManager({
    containerRef: chatContainerRef,
    messages: allMessages,
    shouldAutoScroll: true,
  });

  const handleFeedbackClick = (groupId: string) => {
    const groupedMessage = rawMessages.find(
      (msg) => "id" in msg && msg.attachment?.id?.toString() === groupId,
    );

    if (
      groupedMessage &&
      "attachment" in groupedMessage &&
      groupedMessage.attachment
    ) {
      const attachment = groupedMessage.attachment;

      setFeedbackAttachment({
        id: attachment.id || 0,
        name: getAttachmentDisplayName(attachment) || "attachment",
        path: attachment.url || "",
        url: attachment.url || "",
      });
      setIsFeedbackToolOpen(true);
    }
  };

  const handleSendMessage = async () => {
    if (isTemplate) {
      openUnlock();

      return;
    }
    const hasValidMessageContent =
      messageContent && messageContent.trim().length > 0;
    const hasSuccessfulUploads = successfulUploads.length > 0;

    if (!hasValidMessageContent && !hasSuccessfulUploads) {
      return;
    }

    const isCurrentlyLoading =
      createComment.isPending || updateComment.isPending;

    if (isCurrentlyLoading) return;

    try {
      const attachments = successfulUploads
        .filter((f) => f.attachmentId)
        .map((f) => ({
          attachmentId: f.attachmentId!,
          name: f.file.name,
        }));

      if (isEditing && editMode.messageId) {
        const message = editMode.messages.find(
          (m) => m.asanaResourceType === "story",
        );

        updateComment.mutate(
          {
            attachments: files.map((f) => ({
              attachmentId: f.attachmentId!,
              name: f.file.name,
            })),
            comment: messageContent.trim(),
            commentBatchId: editMode.commentBatchId,
            id: message?.id || editMode.messageId,
            shouldCreateNewMessage: shouldCreateNewMessage(
              messageContent,
              message,
            ),
          },
          {
            onSuccess: () => {
              handleCancelEdit();

              if (!messageContent.includes("@")) return;

              queryClient.invalidateQueries({
                queryKey: requestKeys.requestById(Number(requestId)),
              });

              if (request?.projectId) {
                queryClient.invalidateQueries({
                  queryKey: collaboratorsKeys.collaboratorsByProjectId(
                    request.projectId,
                  ),
                });
              }
            },
          },
        );
      } else {
        createComment.mutate(
          {
            attachments: attachments.length > 0 ? attachments : undefined,
            comment: messageContent.trim(),
          },
          {
            onSuccess: () => {
              handleCancelEdit();

              if (!messageContent.includes("@")) return;

              queryClient.invalidateQueries({
                queryKey: requestKeys.requestById(Number(requestId)),
              });

              if (request?.projectId) {
                queryClient.invalidateQueries({
                  queryKey: collaboratorsKeys.collaboratorsByProjectId(
                    request.projectId,
                  ),
                });
              }
            },
          },
        );
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleFilesSelected = async (newFiles: File[]) => {
    try {
      await addFiles(newFiles);
    } catch (error) {
      console.error("File upload failed:", error);
    }
  };

  const handleRemoveFile = (fileId: string) => {
    const fileToRemove = files.find((f) => f.id === fileId);

    if (fileToRemove) {
      if (fileToRemove.isExisting && fileToRemove.attachmentId) {
        deleteAttachment.mutate(fileToRemove.attachmentId, {
          onError: (error) => {
            console.error("Failed to delete attachment:", error);
          },
          onSuccess: () => {
            removeFile(fileId);
          },
        });
      } else {
        removeFile(fileId);
      }
    }
  };

  const isAnythingLoading =
    createComment.isPending ||
    updateComment.isPending ||
    isUploading ||
    deleteAttachment.isPending;

  return (
    <FeedbackToolProvider>
      <div
        className="w-full h-full flex flex-col chat-section"
        ref={chatPanelRef}
      >
        <div className="flex h-full flex-col gap-2xl">
          <div
            className="flex flex-col gap-[20px] h-full flex-1 overflow-y-auto p-1 chat-section-container relative chat-screen"
            ref={chatContainerRef}
          >
            {isChatConnecting && allMessages.length === 0 ? (
              <ChatSkeleton />
            ) : (
              <>
                <LoadingIndicator isVisible={isLoading} />

                {hasMoreItems && (
                  <div className="h-1 w-full" ref={loadingRef} />
                )}

                <MessageList
                  currentUserId={user?.user.id}
                  handleOpenPreview={handleOpenPreview}
                  isCostumer={isCostumer}
                  isLoading={isLoading}
                  messages={messages}
                  onEditClick={startEdit}
                  onFeedbackClick={handleFeedbackClick}
                  timeZone={timeZone ?? null}
                />
              </>
            )}
          </div>

          {isEditing && (
            <div className="bg-[rgb(62,28,150)] border border-[rgb(62,28,150)] rounded-lg p-1 px-2 mb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <span className="text-white font-medium text-sm">
                    Editing message
                  </span>
                </div>
                <button
                  className="text-white hover:text-white text-sm font-medium px-2 py-1 rounded hover:bg-utility-brand-500 transition-colors"
                  onClick={handleCancelEdit}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {showAfterHoursBanner && (
            <AfterHoursBanner
              onDismiss={handleAfterHoursDismiss}
              onDismissPermanently={handleAfterHoursDismissPermanently}
            />
          )}

          <ChatInput
            canResize={!isCostumer}
            chatPanelRef={chatPanelRef}
            files={files}
            handleFilesSelected={isTemplate ? openUnlock : handleFilesSelected}
            handleSendMessage={isTemplate ? openUnlock : handleSendMessage}
            isEditing={isEditing}
            isLoading={isAnythingLoading}
            isSendDisabled={isLocked}
            isUploadDisabled={isLocked}
            messageContent={messageContent}
            onClearAll={clearAll}
            onRemoveFile={handleRemoveFile}
            onRetryUpload={retryUpload}
            requestId={requestId}
            setMessageContent={setMessageContent}
          />
        </div>
      </div>

      {feedbackAttachment && (
        <FeedbackToolComponent
          attachment={feedbackAttachment}
          open={isFeedbackToolOpen}
          setOpen={setIsFeedbackToolOpen}
        />
      )}

      {currentAttachmentId && (
        <MediaPreviewModal
          alt={
            getAttachmentDisplayName(selectedAttachment?.attachment) ||
            "Chat media"
          }
          defaultSelectedItem={selectedAttachment?.attachment?.id?.toString()}
          mediaItems={mediaItems}
          onClose={handleClosePreview}
          onFeedback={handleFeedbackClick}
          usePreview={true}
        />
      )}
    </FeedbackToolProvider>
  );
}
